/**
 * Contraintes d'environnement et remédiations proposées.
 *
 * Une contrainte détectée sur un serveur ne vaut que si l'on sait quoi en faire. Afficher
 * « restic absent » sans dire ce que cela change ni comment y remédier laisse le travail entier à
 * l'administration, au moment précis où elle découvre l'outil.
 *
 * Ce catalogue est **déterministe et clos**, et c'est un choix. Les contraintes ne viennent pas du
 * serveur : elles sont produites par le code de détection de Vigie, donc leur liste est connue à la
 * compilation. Une correspondance figée est ici complète par construction, testable, et ne peut pas
 * inventer une commande hasardeuse à exécuter en production — ce qu'aucune analyse générative ne
 * peut garantir. Un test vérifie que toute contrainte déclarée possède sa remédiation : ajouter un
 * code sans l'expliquer casse la compilation ou le test, jamais l'expérience de l'utilisatrice.
 */
export const RESTRICTION_CODES = [
    'systemd_absent',
    'restic_absent',
    'mysqldump_absent',
    'tar_absent',
];
/** Actions que Vigie sait mener elle-même, par la connexion SSH déjà ouverte. */
export const REMEDIATION_ACTIONS = ['pousser_restic'];
/**
 * Remédiations, une par contrainte.
 *
 * Chaque entrée propose au moins l'option « ne rien faire », avec ce qu'elle coûte : une contrainte
 * n'est presque jamais bloquante, elle dégrade. Le dire évite de faire croire qu'il faut résoudre
 * avant de continuer.
 */
export const REMEDIATIONS = {
    systemd_absent: {
        titre: 'systemd absent',
        consequence: 'Le redémarrage par systemctl est impossible : Vigie ne pourra pas relancer le site après une modification de configuration ou une mise à jour, à moins qu’un autre moyen ne soit déclaré.',
        options: [
            {
                titre: 'Retenir le type « ghost-cli sans systemd »',
                effet: 'Vigie redémarre par « ghost restart » depuis le dossier de l’installation. C’est le cas normal d’un hébergement mutualisé.',
                parDefaut: true,
            },
            {
                titre: 'Retenir le type « personnalisé » et fournir la commande',
                effet: 'À utiliser si le site est relancé par un script maison ou un superviseur autre que systemd. La commande sera exécutée telle quelle, sans interprétation.',
            },
        ],
    },
    restic_absent: {
        titre: 'restic absent',
        consequence: 'Sans restic, pas de dépôt dédupliqué ni de restauration par composant : les sauvegardes transitent par le plan de contrôle et sont déposées en une archive chiffrée par sauvegarde.',
        options: [
            {
                titre: 'Laisser Vigie pousser restic',
                effet: 'Vigie télécharge la version correspondant à l’architecture du serveur, vérifie son empreinte SHA256, et dépose le binaire dans ~/.vigie/bin. Aucun droit root n’est nécessaire, rien n’est installé à l’échelle du système, et le binaire du serveur est préféré s’il apparaît plus tard.',
                action: 'pousser_restic',
            },
            {
                titre: 'Installer restic par le gestionnaire de paquets',
                effet: 'Préférable si tu administres ce serveur : la mise à jour du binaire suit alors celle du système.',
                commande: 'sudo apt-get update && sudo apt-get install -y restic',
            },
            {
                titre: 'Ne rien faire : sauvegardes en mode streamed',
                effet: 'Parfaitement viable, et c’est le mode retenu sur un hébergement où l’on ne peut rien installer. La restauration reste complète, mais porte sur l’archive entière plutôt que sur un composant.',
                parDefaut: true,
            },
        ],
    },
    mysqldump_absent: {
        titre: 'mysqldump absent',
        consequence: 'La base MySQL ne peut pas être sauvegardée depuis l’hôte. Les fichiers seraient sauvegardés sans leur base — une sauvegarde qui semble réussir et ne permet pas de repartir.',
        options: [
            {
                titre: 'Installer le client MySQL',
                effet: 'Fournit mysqldump. C’est la solution si la base tourne sur cet hôte ou sur un serveur joignable depuis lui.',
                commande: 'sudo apt-get update && sudo apt-get install -y default-mysql-client',
            },
            {
                titre: 'Passer par le conteneur, si la base est dans Docker',
                effet: 'Vigie exécute mysqldump à l’intérieur du conteneur de base. Rien à installer : il suffit de retenir le type « docker compose » et de renseigner le service de base de données.',
            },
            {
                titre: 'Vérifier que le site utilise bien SQLite',
                effet: 'Une installation Ghost sur SQLite n’a pas besoin de mysqldump : le fichier est sauvegardé directement. La détection l’indique dans les paramètres d’installation.',
                parDefaut: true,
            },
        ],
    },
    tar_absent: {
        titre: 'tar absent',
        consequence: 'Le dossier content/ — images, thèmes, fichiers téléversés — ne peut pas être archivé. C’est la partie irremplaçable d’un site Ghost : sans elle, une restauration ne rend que les articles.',
        options: [
            {
                titre: 'Installer tar',
                effet: 'Presque toujours disponible ; son absence signale un environnement très restreint. Une fois installé, relance le test de connexion.',
                commande: 'sudo apt-get update && sudo apt-get install -y tar',
            },
            {
                titre: 'Sauvegarder ce site autrement',
                effet: 'Si rien ne peut être installé, ce serveur ne peut pas être sauvegardé par Vigie. Mieux vaut le savoir maintenant que de découvrir des sauvegardes vides après un incident.',
                parDefaut: true,
            },
        ],
    },
};
//# sourceMappingURL=remediations.js.map